/**
 * A tiny synchronous pub/sub used for two things:
 *  - internal decoupling (the registry tells the embed layer to re-scan)
 *  - letting the host Webflow page react to widget activity without polling
 *
 * Handlers are isolated: a listener registered by page code that throws must
 * never take down a mount or another listener.
 */

/**
 * Known events. Extend via declaration merging from feature code:
 *
 *   declare module './events' {
 *     interface CodeverseEventMap {
 *       'auth:change': { userId: string | null }
 *     }
 *   }
 */
export interface CodeverseEventMap {
  'component:mounted': { name: string; element: Element }
  'component:unmounted': { name: string; element: Element }
  'component:error': { name: string; error: unknown }
  'component:missing': { name: string; element: Element }
  'registry:changed': { name: string }
  ready: { version: string }
}

/** Known event names keep autocomplete; arbitrary strings are still allowed. */
export type EventName = keyof CodeverseEventMap | (string & {})

export type PayloadOf<E extends EventName> = E extends keyof CodeverseEventMap
  ? CodeverseEventMap[E]
  : unknown

export type Handler<E extends EventName> = (payload: PayloadOf<E>) => void

type AnyHandler = (payload: never) => void

const handlers = new Map<string, Set<AnyHandler>>()

/** Subscribe to an event. Returns an unsubscribe function. */
export function on<E extends EventName>(event: E, handler: Handler<E>): () => void {
  let set = handlers.get(event)
  if (!set) {
    set = new Set()
    handlers.set(event, set)
  }
  set.add(handler as AnyHandler)
  return () => off(event, handler)
}

/** Unsubscribe a previously registered handler. */
export function off<E extends EventName>(event: E, handler: Handler<E>): void {
  const set = handlers.get(event)
  if (!set) return
  set.delete(handler as AnyHandler)
  if (set.size === 0) handlers.delete(event)
}

/** Emit an event. Handler exceptions are logged, never propagated. */
export function emit<E extends EventName>(event: E, payload: PayloadOf<E>): void {
  const set = handlers.get(event)
  if (!set) return
  // Copy first so a handler that unsubscribes mid-emit can't disturb iteration.
  for (const handler of [...set]) {
    try {
      ;(handler as Handler<E>)(payload)
    } catch (error) {
      console.error(`[codeverse] listener for "${event}" threw`, error)
    }
  }
}

/** Test helper — drops every subscription. */
export function resetEvents(): void {
  handlers.clear()
}
